/**
 * MurtiLeb product page: gallery, variant pills, quantity, add to cart / buy now,
 * gift-wrap add-on and the mobile sticky bar. Cart updates go through Horizon's
 * CartLinesUpdateEvent so the header count and cart drawer refresh themselves.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const root = document.querySelector('[data-ml-pdp]');

if (root) {
  const data = JSON.parse(document.querySelector('[data-ml-product-json]')?.textContent || '{"variants":[]}');
  const form = root.querySelector('[data-ml-pdp-form]');
  const idInput = form.querySelector('[data-ml-variant-id]');
  const qtyInput = form.querySelector('[data-ml-qty-input]');
  const atc = form.querySelector('[data-ml-atc]');
  const buy = form.querySelector('[data-ml-buy]');
  const errorEl = form.querySelector('[data-ml-error]');
  const sticky = document.querySelector('[data-ml-sticky]');
  const track = root.querySelector('[data-ml-gallery-track]');
  const thumbs = [...root.querySelectorAll('[data-ml-thumb]')];

  /* ---------- Gallery ---------- */
  let currentSlideId = null;
  const markCurrentSlide = (mediaId) => {
    if (!track || String(mediaId) === currentSlideId) return;
    currentSlideId = String(mediaId);
    track.querySelectorAll('.ml-gallery__slide').forEach((slide) => {
      const on = slide.dataset.mediaId === currentSlideId;
      if (on) {
        slide.classList.remove('is-current');
        void slide.offsetWidth; // restart the fade-in animation
      }
      slide.classList.toggle('is-current', on);
    });
  };

  const setActiveThumb = (mediaId) => {
    markCurrentSlide(mediaId);
    thumbs.forEach((t) => {
      const on = t.dataset.mlThumb === String(mediaId);
      t.classList.toggle('is-active', on);
      if (on) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  };

  const showMedia = (mediaId, smooth = true) => {
    const slide = track?.querySelector(`[data-media-id="${mediaId}"]`);
    if (!slide) return;
    track.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    setActiveThumb(mediaId);
  };

  thumbs.forEach((t) => t.addEventListener('click', () => showMedia(t.dataset.mlThumb)));
  const firstSlide = track?.querySelector('.ml-gallery__slide');
  if (firstSlide) markCurrentSlide(firstSlide.dataset.mediaId);

  track?.addEventListener(
    'scroll',
    () => {
      const index = Math.round(track.scrollLeft / (track.clientWidth || 1));
      const slide = track.children[index];
      if (slide) {
        setActiveThumb(slide.dataset.mediaId);
        const thumb = thumbs.find((t) => t.dataset.mlThumb === slide.dataset.mediaId);
        thumb?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
      // Pause videos that scrolled away.
      track.querySelectorAll('video').forEach((v) => {
        if (!v.closest('.ml-gallery__slide')?.isSameNode(slide)) v.pause();
      });
    },
    { passive: true }
  );

  /* ---------- Quantity ---------- */
  const minusBtn = form.querySelector('[data-ml-qty="-1"]');
  const syncQty = () => {
    const value = Math.min(Math.max(1, parseInt(qtyInput.value, 10) || 1), Number(qtyInput.max) || 99);
    qtyInput.value = String(value);
    if (minusBtn) minusBtn.disabled = value <= 1;
  };
  form.querySelectorAll('[data-ml-qty]').forEach((btn) =>
    btn.addEventListener('click', () => {
      qtyInput.value = String((parseInt(qtyInput.value, 10) || 1) + Number(btn.dataset.mlQty));
      syncQty();
    })
  );
  qtyInput.addEventListener('change', syncQty);

  /* ---------- Variants ---------- */
  const optionInputs = [...form.querySelectorAll('[data-ml-option]')];

  const selectedOptions = () => {
    const values = [];
    optionInputs.forEach((input) => {
      if (input.checked) values[Number(input.dataset.mlOption)] = input.value;
    });
    return values;
  };

  const findVariant = (values) => data.variants.find((v) => v.options.every((o, i) => o === values[i]));

  const setAll = (selector, fn) => document.querySelectorAll(selector).forEach(fn);

  const updateAvailability = () => {
    // Strike through values that have no available variant with the other current selections.
    const values = selectedOptions();
    optionInputs.forEach((input) => {
      const pos = Number(input.dataset.mlOption);
      const test = [...values];
      test[pos] = input.value;
      const match = findVariant(test);
      input.closest('.ml-options__pill')?.classList.toggle('is-unavailable', !match || !match.available);
    });
  };

  const applyVariant = (variant) => {
    const available = Boolean(variant?.available);
    if (variant) idInput.value = String(variant.id);

    [atc, buy, sticky?.querySelector('[data-ml-sticky-atc]'), sticky?.querySelector('[data-ml-sticky-buy]')].forEach((b) => {
      if (b) b.disabled = !available;
    });
    const label = form.querySelector('[data-ml-atc-label]');
    if (label) label.textContent = !variant ? 'Unavailable' : available ? 'Add to cart' : 'Sold out';

    if (variant) {
      setAll('[data-ml-price-now]', (el) => (el.textContent = variant.price));
      setAll('[data-ml-price-was]', (el) => {
        el.textContent = variant.compare || '';
        el.hidden = !variant.compare;
      });
      const save = root.querySelector('[data-ml-save]');
      if (save) {
        save.hidden = !variant.save;
        const amount = save.querySelector('[data-ml-save-amount]');
        if (amount && variant.save) amount.textContent = variant.save;
      }
      setAll('[data-ml-prepaid-price]', (el) => {
        if (variant.prepaid) el.textContent = variant.prepaid;
      });
      setAll('[data-ml-price-off]', (el) => {
        el.textContent = variant.off ? `${variant.off}% OFF` : '';
        el.hidden = !variant.off;
      });

      const stock = form.querySelector('[data-ml-stock]');
      if (stock) {
        stock.hidden = !variant.stock;
        const count = stock.querySelector('[data-ml-stock-count]');
        if (count && variant.stock) count.textContent = String(variant.stock);
      }

      if (variant.media) showMedia(variant.media);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    }

    selectedOptions().forEach((value, i) => {
      const labelEl = form.querySelector(`[data-ml-option-label="${i}"]`);
      if (labelEl) labelEl.textContent = value;
    });
    updateAvailability();
  };

  optionInputs.forEach((input) => input.addEventListener('change', () => applyVariant(findVariant(selectedOptions()))));
  if (optionInputs.length) updateAvailability();

  /* ---------- Add to cart / buy now ---------- */
  const showError = (message) => {
    errorEl.textContent = message;
    errorEl.hidden = !message;
  };

  const addToCart = async (button, { announce = true } = {}) => {
    showError('');
    const quantity = Math.max(1, parseInt(qtyInput.value, 10) || 1);
    const items = [{ id: Number(idInput.value), quantity }];
    const gift = form.querySelector('[data-ml-gift]');
    if (gift?.checked) items.push({ id: Number(gift.dataset.mlGift), quantity: 1 });

    button?.setAttribute('aria-busy', 'true');
    const deferred = CartLinesUpdateEvent.createPromise();
    if (announce) {
      form.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        })
      );
    } else {
      deferred.promise.catch(() => {});
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const didError = Boolean(result.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError, items: cart.items, source: 'ml-product', itemCount: quantity },
      });
      if (didError) {
        const message = result.description || result.message || 'Could not add to cart';
        form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        showError(message);
        return false;
      }
      return true;
    } catch (error) {
      deferred.reject(error);
      showError('Network problem — please try again.');
      return false;
    } finally {
      button?.removeAttribute('aria-busy');
    }
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (atc.disabled) return;
    await addToCart(atc);
  });

  const buyButtons = () => [buy, sticky?.querySelector('[data-ml-sticky-buy]')].filter(Boolean);
  const magicButton = () => form.querySelector('[data-ml-magic] magic-checkout-btn');

  // Razorpay opens sooner when its servers are already connected. Warm the connections as the
  // shopper reaches for a Buy button (hover, touch or focus), a moment before the click lands.
  let checkoutWarmed = false;
  const warmCheckout = () => {
    if (checkoutWarmed || !magicButton()) return;
    checkoutWarmed = true;
    ['https://api.razorpay.com', 'https://checkout.razorpay.com', 'https://cdn.razorpay.com'].forEach((href) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      document.head.appendChild(link);
    });
  };

  // Show a spinner on the Buy buttons at once, until Razorpay's checkout is on screen.
  const showOpening = () => {
    const buttons = buyButtons();
    buttons.forEach((b) => {
      b.classList.add('is-opening');
      b.setAttribute('aria-busy', 'true');
    });
    const started = Date.now();
    const timer = window.setInterval(() => {
      const box = document.querySelector('.razorpay-container');
      const open = box && getComputedStyle(box).display !== 'none';
      if (open || Date.now() - started > 10000) {
        window.clearInterval(timer);
        buttons.forEach((b) => {
          b.classList.remove('is-opening');
          b.removeAttribute('aria-busy');
        });
      }
    }, 120);
  };

  // Shiprocket checkout: its snippet hides our Buy buttons and builds its own once its script has
  // loaded, which can take a few seconds on phones. Until then our button stays visible (CSS), and a
  // tap waits for Shiprocket's button next to it and presses that, so the first tap always counts.
  const waitFor = (find, timeout) =>
    new Promise((resolve) => {
      const started = Date.now();
      const check = () => {
        const found = find();
        if (found || Date.now() - started > timeout) resolve(found || null);
        else window.setTimeout(check, 100);
      };
      check();
    });

  const shiprocketBuy = async (button) => {
    const box = button.closest('[data-ml-actions], [data-ml-sticky]')?.querySelector('.shiprocket-headless');
    if (!box) return false;
    const buttons = buyButtons();
    buttons.forEach((b) => b.classList.add('is-opening'));
    const srButton = await waitFor(() => box.querySelector('.sr-headless-checkout'), 6000);
    buttons.forEach((b) => b.classList.remove('is-opening'));
    if (!srButton) return false; // Shiprocket did not load: fall back to the normal checkout
    srButton.click();
    return true;
  };

  const buyNow = async (button) => {
    if (buy.disabled || buy.classList.contains('is-opening')) return;
    if (await shiprocketBuy(button)) return;
    // Razorpay Magic Checkout, when installed, adds <magic-checkout-btn> inside our hidden hook.
    // It reads the variant and quantity from this form and opens its own checkout.
    const magic = magicButton();
    const giftChecked = form.querySelector('[data-ml-gift]')?.checked;
    if (magic && !giftChecked) {
      showOpening();
      magic.click();
      return;
    }
    // Buy now goes straight to checkout, so it does not announce the add (no drawer flash).
    const ok = await addToCart(button, { announce: false });
    if (ok) window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
  };

  buy.addEventListener('click', () => buyNow(buy));
  ['pointerenter', 'touchstart', 'focus'].forEach((type) => {
    buyButtons().forEach((b) => b.addEventListener(type, warmCheckout, { passive: true }));
  });

  /* ---------- Sticky bar ---------- */
  // Shiprocket's Buy now in the sticky bar reads its own small form; copy the shopper's current
  // variant and quantity into it just before Shiprocket's click handler runs.
  const stickySr = document.querySelector('[data-ml-sticky-sr]');
  if (stickySr) {
    stickySr.addEventListener(
      'click',
      () => {
        const id = form.querySelector('[name="id"]')?.value;
        const qty = form.querySelector('[name="quantity"]')?.value || '1';
        if (id) stickySr.querySelector('[name="id"]').value = id;
        stickySr.querySelector('[name="quantity"]').value = qty;
      },
      true
    );
    stickySr.addEventListener('submit', (event) => event.preventDefault());
  }

  if (sticky) {
    sticky.querySelector('[data-ml-sticky-atc]')?.addEventListener('click', (e) => addToCart(e.currentTarget));
    sticky.querySelector('[data-ml-sticky-buy]')?.addEventListener('click', (e) => buyNow(e.currentTarget));
    // The bar is always pinned on mobile (CSS); nothing to toggle here.
  }

  /* ---------- Pincode delivery checker ---------- */
  const pin = root.querySelector('[data-ml-pin]');
  if (pin) {
    const input = pin.querySelector('[data-ml-pin-input]');
    const result = pin.querySelector('[data-ml-pin-result]');
    const minDays = Number(pin.dataset.minDays) || 4;
    const maxDays = Number(pin.dataset.maxDays) || minDays;
    const PIN_KEY = 'murtileb:pincode';

    const fmt = (days) => {
      const d = new Date();
      d.setDate(d.getDate() + days);
      return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    };

    const check = (value) => {
      const code = value.trim();
      result.hidden = false;
      // Indian pincodes are 6 digits and never start with 0.
      if (!/^[1-9][0-9]{5}$/.test(code)) {
        result.classList.add('is-error');
        result.textContent = 'Please enter a valid 6-digit pincode.';
        return;
      }
      result.classList.remove('is-error');
      const range = minDays === maxDays ? `${minDays} days` : `${minDays}–${maxDays} days`;
      result.textContent = `✓ Delivery to ${code} in ${range} — by ${fmt(minDays)}${minDays === maxDays ? '' : ` – ${fmt(maxDays)}`}.`;
      try {
        localStorage.setItem(PIN_KEY, code);
      } catch {
        /* storage unavailable */
      }
    };

    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
    });
    pin.querySelector('[data-ml-pin-form]').addEventListener('submit', (event) => {
      event.preventDefault();
      check(input.value);
    });

    try {
      const saved = localStorage.getItem(PIN_KEY);
      if (saved) {
        input.value = saved;
        check(saved);
      }
    } catch {
      /* storage unavailable */
    }
  }

  /* ---------- Delivery date ---------- */
  const eta = root.querySelector('[data-ml-eta]');
  if (eta) {
    const addDays = (n) => {
      const d = new Date();
      d.setDate(d.getDate() + n);
      return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    };
    const min = Number(eta.dataset.minDays) || 4;
    const max = Number(eta.dataset.maxDays) || min;
    eta.textContent = `Get it by ${addDays(min)}${max > min ? ` – ${addDays(max)}` : ''}`;
    eta.hidden = false;
  }

  /* ---------- Image zoom ---------- */
  const zoom = document.querySelector('[data-ml-zoom]');
  const zoomImgs = [...root.querySelectorAll('[data-ml-zoom-src]')];
  if (zoom && zoomImgs.length && typeof zoom.showModal === 'function') {
    const img = zoom.querySelector('[data-ml-zoom-img]');
    const count = zoom.querySelector('[data-ml-zoom-count]');
    let current = 0;
    const show = (i) => {
      current = (i + zoomImgs.length) % zoomImgs.length;
      img.src = zoomImgs[current].dataset.mlZoomSrc;
      img.alt = zoomImgs[current].alt || '';
      count.textContent = `${current + 1} / ${zoomImgs.length}`;
      zoom.querySelector('.ml-zoom__stage').scrollTo(0, 0);
    };
    zoomImgs.forEach((el, i) =>
      el.addEventListener('click', () => {
        show(i);
        zoom.showModal();
      })
    );
    zoom.querySelector('[data-ml-zoom-close]').addEventListener('click', () => zoom.close());
    zoom.querySelector('[data-ml-zoom-prev]').addEventListener('click', () => show(current - 1));
    zoom.querySelector('[data-ml-zoom-next]').addEventListener('click', () => show(current + 1));
    zoom.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') show(current - 1);
      if (e.key === 'ArrowRight') show(current + 1);
    });
    const single = zoomImgs.length < 2;
    zoom.querySelectorAll('.ml-zoom__nav, .ml-zoom__count').forEach((el) => (el.hidden = single));
  }

  /* ---------- Share ---------- */
  root.querySelector('[data-ml-share]')?.addEventListener('click', async (event) => {
    const btn = event.currentTarget;
    const payload = { title: btn.dataset.title, url: btn.dataset.url };
    try {
      if (navigator.share) await navigator.share(payload);
      else {
        await navigator.clipboard.writeText(payload.url);
        btn.setAttribute('aria-label', 'Link copied');
      }
    } catch {
      /* user cancelled */
    }
  });
}
